import main from "@/widget-context/main";
import theme from "@/widget-context/theme";
import type { Root } from "react-dom/client";

const AUTO_CONTAINER_ID = "chatbot-widget-root";
let mounted = false;
let reactRoot: Root | null = null;

function isMounted(): boolean {
  const existingContainer = document.getElementById(AUTO_CONTAINER_ID);
  return mounted && !!existingContainer && existingContainer.children.length > 0;
}

function mount(options: any = {}) {
  if (isMounted()) {
    console.warn('[ChatbotWidget] mount() called but widget is already mounted. Emitting chatbotInitialized event.');
    window.dispatchEvent(new CustomEvent('chatbotInitialized'));
    return;
  }
  
  let container = options.container as HTMLElement | undefined | null;
  if (!container) {
    container = document.getElementById(AUTO_CONTAINER_ID);
    if (!container) {
      container = document.createElement("div");
      container.id = AUTO_CONTAINER_ID;
      container.style.position = "fixed";
      container.style.bottom = "0";
      container.style.right = "0";
      container.style.zIndex = theme["--z-index-chatbot-widget-root"] || "2147483647";
      document.body.appendChild(container);
    }
  }
  
  console.log('[ChatbotWidget] Mounting widget');
  reactRoot = main(container, options);
  mounted = true;
}

function openChat(context?: string) {
  const event = new CustomEvent('chatbotOpen', {
    detail: context ? { context } : undefined
  });
  window.dispatchEvent(event);
}

function setContext(context?: string) {
  const event = new CustomEvent('chatbotSetContext', {
    detail: context ? { context } : undefined
  });
  window.dispatchEvent(event);
}

function minimizeChat() {
  const event = new CustomEvent('chatbotMinimize');
  window.dispatchEvent(event);
}

function closeChat() {
  const event = new CustomEvent('chatbotClose');
  window.dispatchEvent(event);
}

function startLivechat() {
  const event = new CustomEvent('chatbotStartLivechatRequested');
  window.dispatchEvent(event);
}

function sendMessage(message: string, options?: { autoPost?: boolean }) {
  const event = new CustomEvent('chatbotSendMessage', {
    detail: { message, options }
  });
  window.dispatchEvent(event);
}

function unmount() {
  if (!isMounted()) {
    console.warn('[ChatbotWidget] unmount() called but widget is not mounted.');
    return;
  }
  
  console.log('[ChatbotWidget] Unmounting widget');
  
  try {
    if (reactRoot) {
      reactRoot.unmount();
      reactRoot = null;
    }

    const container = document.getElementById(AUTO_CONTAINER_ID);
    if (container) {
      container.remove();
    }
  } catch (error) {
    console.error('[ChatbotWidget] Error during unmount:', error);
  } finally {
    mounted = false;
  }
}

(window as any).ChatbotWidget = { 
  mount, 
  unmount,
  isMounted,
  openChat,
  setContext,
  minimizeChat,
  closeChat,
  startLivechat,
  sendMessage
};

window.dispatchEvent(new CustomEvent('chatbotApiLoaded'));

export { mount, unmount, isMounted, openChat, setContext, minimizeChat, closeChat, startLivechat, sendMessage };